import { useApp } from "@/context/AppContext";
import { Accessibility, Volume2, Contrast, Type, Zap, Globe } from "lucide-react";

const LANGS = [
  { code: "en-US", label: "English" },
  { code: "en-IN", label: "English (India)" },
  { code: "hi-IN", label: "हिन्दी" },
  { code: "es-ES", label: "Español" },
  { code: "fr-FR", label: "Français" },
  { code: "pt-BR", label: "Português" },
  { code: "ar-SA", label: "العربية" },
];

export function AccessibilityMode() {
  const {
    a11yLarge,
    setA11yLarge,
    a11yContrast,
    setA11yContrast,
    reduceMotion,
    setReduceMotion,
    voiceOn,
    setVoiceOn,
    language,
    setLanguage,
    speak,
  } = useApp();

  return (
    <section id="accessibility" className="relative py-20 md:py-28 border-t border-white/5">
      <div className="absolute inset-0 tac-grid-fine opacity-30 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3 mb-4">
          <span className="font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-cyan)]">STEP 06 // ACCESSIBILITY MODE</span>
          <span className="h-px w-14 bg-[color:var(--lg-line-strong)]" />
        </div>
        <h2 className="font-display text-3xl sm:text-5xl font-extrabold uppercase tracking-tight max-w-3xl">
          Built to be used <span className="text-lime-accent">when it&apos;s hardest</span>.
        </h2>
        <p className="mt-3 max-w-2xl text-[color:var(--lg-text-2)]">
          Large tap targets, high contrast, screen-reader-friendly labels, reduced motion, and browser voice — one tap
          each.
        </p>

        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Toggle
            icon={<Type size={18} />}
            title="Large mode"
            desc="Bigger typography and buttons across the entire app."
            active={a11yLarge}
            onClick={() => setA11yLarge(!a11yLarge)}
            testid="a11y-large-toggle"
          />
          <Toggle
            icon={<Contrast size={18} />}
            title="High contrast"
            desc="Stronger borders and text for low-light or bright sun."
            active={a11yContrast}
            onClick={() => setA11yContrast(!a11yContrast)}
            testid="a11y-contrast-toggle"
          />
          <Toggle
            icon={<Zap size={18} />}
            title="Reduce motion"
            desc="Disable animations and transitions."
            active={reduceMotion}
            onClick={() => setReduceMotion(!reduceMotion)}
            testid="a11y-motion-toggle"
          />
          <Toggle
            icon={<Volume2 size={18} />}
            title="Voice mode"
            desc="Instructions are read aloud when supported."
            active={voiceOn}
            onClick={() => {
              const next = !voiceOn;
              setVoiceOn(next);
              if (next) setTimeout(() => speak("Voice mode is on. LIFEGRID will speak instructions."), 100);
            }}
            testid="a11y-voice-toggle"
          />
          <div className="glass rounded-md p-4">
            <div className="flex items-center gap-2 font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-lime)]">
              <Globe size={16} /> LANGUAGE
            </div>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="mt-3 w-full bg-transparent border border-white/15 rounded-sm px-3 py-2 focus:border-[color:var(--lg-lime)] focus:outline-none"
              data-testid="a11y-language"
            >
              {LANGS.map((l) => (
                <option key={l.code} value={l.code} className="bg-[color:var(--lg-void)]">
                  {l.label}
                </option>
              ))}
            </select>
            <button className="btn-tactile mt-3 w-full" onClick={() => speak("This is a LIFEGRID voice test.")} data-testid="a11y-test-voice">
              Test voice
            </button>
          </div>

          <a href="tel:112" className="glass rounded-md p-4 flex flex-col justify-between tac-glow-red" data-testid="a11y-emergency">
            <div className="flex items-center gap-2 font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-red)]">
              <Accessibility size={16} /> LARGE EMERGENCY BUTTON
            </div>
            <div className="mt-4 font-display text-3xl font-extrabold uppercase">Call for help</div>
            <div className="text-xs text-[color:var(--lg-text-3)] mt-1">Uses tel:112 (adjust locally)</div>
          </a>
        </div>
      </div>
    </section>
  );
}

function Toggle({ icon, title, desc, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      className={`glass rounded-md p-4 text-left transition ${active ? "tac-glow-lime border-[color:var(--lg-lime)]/60" : ""}`}
      data-testid={testid}
      aria-pressed={active}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-lime)]">
          {icon} {title.toUpperCase()}
        </div>
        <div
          className={`h-5 w-9 rounded-full relative transition ${
            active ? "bg-[color:var(--lg-lime)]" : "bg-white/10"
          }`}
        >
          <span
            className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${
              active ? "left-4" : "left-0.5"
            }`}
          />
        </div>
      </div>
      <div className="mt-2 text-sm text-[color:var(--lg-text-2)]">{desc}</div>
    </button>
  );
}
